import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon,
  CloudArrowDownIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  CreditCardIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const WorkflowIntegrationPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const workflowSteps = [
    {
      id: 'advisor-onboarding',
      name: 'Advisor Onboarding',
      description: 'Getting started with SharpeMetrix model portfolios and accessing your preferred models.',
      icon: UserGroupIcon,
      details: `The first step in implementing SharpeMetrix models is selecting and accessing your preferred portfolio strategy:

      Model Selection Process:
      • Navigate to the Models tab on your SharpeMetrix Dashboard
      • Review performance metrics, holdings, and risk characteristics for each model
      • Select the model that best aligns with your investment philosophy and client needs
      • Access detailed model information including historical performance and current allocations

      Accessing Holdings and Trade Lists:
      • Click on the Holdings tab for your selected model
      • Export trade lists via the Download CSV button for easy implementation
      • Alternative delivery options include direct file delivery or API feeds (under development)
      • Models typically rebalance on the 1st trading day of each month

      Note: Refer to the Monthly Rebalancing Process section for detailed rebalancing schedules and procedures.`
    },
    {
      id: 'compliance-review',
      name: 'Compliance Review',
      description: 'Ensuring proper due diligence documentation for internal compliance procedures.',
      icon: ShieldCheckIcon,
      details: `Your RIA's compliance team must complete proper due diligence documentation before implementing any SharpeMetrix models:

      Required Documentation:
      • Review and document the investment methodology and process
      • Assess the model's alignment with your firm's investment philosophy
      • Evaluate risk management procedures and historical performance
      • Document suitability for different client types and risk profiles

      Compliance Checklist:
      • Investment strategy documentation and rationale
      • Risk assessment and management procedures
      • Performance history and benchmark comparisons
      • Fee structure and cost analysis
      • Ongoing monitoring and review procedures

      This documentation should follow your firm's standard due diligence procedures for third-party investment managers.`
    },
    {
      id: 'model-delivery',
      name: 'Model Delivery Options',
      description: 'Multiple ways to receive and implement SharpeMetrix model updates and holdings.',
      icon: CloudArrowDownIcon,
      details: `SharpeMetrix models can be delivered through several channels to fit your operational workflow:

      Current Delivery Methods:
      • SharpeMetrix Direct Files: Download CSV or PDF files with weights and tickers directly from the platform
      • Manual Export: Use the Dashboard to export current holdings and rebalance instructions

      Future Delivery Options (Under Development):
      • SharpeMetrix API Feeds: Automated delivery of model holdings and rebalance instructions 🔄 Coming Soon
      • Custodian Model Marketplace: Integration with platforms like Schwab, Fidelity, Pershing, Envestnet, Orion 🔄 Coming Soon
      • TAMP Integration: Direct integration with turnkey platforms for automated trading and billing 🔄 Coming Soon

      Choose the delivery method that best integrates with your existing technology stack and operational procedures.`
    },
    {
      id: 'investment-committee',
      name: 'RIA Investment Committee Approval',
      description: 'Internal review and approval process for model implementation.',
      icon: DocumentDuplicateIcon,
      details: `Your RIA's Investment Committee should review and formally approve SharpeMetrix models before client implementation:

      Review Process:
      • Analyze model allocations and underlying security selection methodology
      • Evaluate risk and return assumptions against your firm's expectations
      • Assess suitability for different client segments and risk profiles
      • Review fee structure and impact on overall client costs

      Documentation Tools:
      • Use the Download PDF button to export detailed Model Factsheets
      • Customize factsheets with your firm's branding and disclaimers (🔄 White-label option under development)
      • Document committee decisions and approval rationale
      • Establish ongoing monitoring and review procedures

      The Investment Committee should establish clear criteria for model selection, monitoring, and potential replacement.`
    },
    {
      id: 'client-alignment',
      name: 'Client Alignment',
      description: 'Matching client goals and risk tolerance with appropriate model portfolios.',
      icon: UsersIcon,
      details: `Proper client-model alignment is crucial for successful implementation and client satisfaction:

      Client Assessment:
      • Review client investment objectives and time horizon
      • Assess risk tolerance through questionnaires and discussions
      • Evaluate existing portfolio and transition considerations
      • Ensure alignment with Investment Policy Statement (IPS)

      Model Matching Process:
      • Map client risk profiles to appropriate SharpeMetrix models
      • Consider factors like volatility tolerance, return expectations, and liquidity needs
      • Document the rationale for model selection in client files
      • Discuss model characteristics and expected outcomes with clients

      Suitability Documentation:
      • Maintain records of client risk assessment
      • Document model selection rationale
      • Keep records of client discussions and approvals
      • Update documentation as client circumstances change`
    },
    {
      id: 'trading-implementation',
      name: 'Trading & Implementation',
      description: 'Executing model trades through various custodial and platform arrangements.',
      icon: Cog6ToothIcon,
      details: `Model implementation varies depending on your custodial and technology arrangements:

      Implementation Options:

      Custodian-Based Implementation:
      • Sleeved at Custodian: Custodian automatically trades accounts using SharpeMetrix models (similar to Schwab's Model Market Center)
      • Automated execution based on model updates
      • Integrated billing and reporting

      TAMP-Based Implementation:
      • TAMP receives portfolio rebalance signals from SharpeMetrix
      • Automated trade execution across client accounts
      • Consolidated reporting and billing

      Manual RIA Implementation:
      • Download CSV trade lists from SharpeMetrix Dashboard
      • Manually input trades into custodial trading systems
      • Monitor execution and manage any trade exceptions
      • Coordinate rebalancing across multiple client accounts

      Choose the implementation method that best fits your operational capabilities and client service model.`
    },
    {
      id: 'ongoing-maintenance',
      name: 'Ongoing Maintenance',
      description: 'Managing model updates, rebalancing, and ongoing client service.',
      icon: ArrowPathIcon,
      details: `Successful model implementation requires ongoing maintenance and monitoring:

      Model Updates:
      • Monthly rebalancing typically occurs on the 1st trading day of each month
      • Receive updates through your chosen delivery method (manual export currently available)
      • 🔄 Automated integration under development for seamless updates

      Execution Management:
      • Share CSV rebalance files with custodian or TAMP for automated execution
      • Monitor trade execution and resolve any issues
      • Manage cash flows and client-specific modifications
      • Track performance versus model benchmarks

      Client Communication:
      • Provide regular performance reports and model updates
      • Explain significant portfolio changes or market events
      • Conduct periodic reviews of model suitability
      • Document all client interactions and decisions

      Administrative Tasks:
      • Maintain compliance documentation
      • Monitor fee calculations and billing
      • Update client records and IPS as needed
      • Coordinate with custodians and service providers`
    },
    {
      id: 'billing-fees',
      name: 'Billing & Fee Structure',
      description: 'Understanding fee arrangements and billing procedures for SharpeMetrix models.',
      icon: CreditCardIcon,
      details: `SharpeMetrix operates on a model-only basis with transparent fee arrangements:

      SharpeMetrix Fees:
      • SharpeMetrix provides model weights and rebalancing instructions only
      • No account management, trading, or billing services provided
      • Fees are separate from and in addition to advisor management fees

      Advisor Billing:
      • Advisors continue to bill clients using their standard AUM fee structure
      • SharpeMetrix model fees are typically passed through to clients
      • Clear disclosure of all fees in client agreements is required

      Platform-Based Billing:
      • TAMP arrangements may involve fee sharing between advisor, TAMP, and model strategist
      • Custodian-based implementations typically maintain separate fee structures
      • All fee arrangements should be clearly documented and disclosed

      Fee Transparency:
      • Provide clients with clear breakdown of all fees
      • Explain the value proposition of professional model management
      • Document fee arrangements in client agreements and IPS
      • Regularly review fee competitiveness and client value`
    }
  ];

  const workflowSummary = {
    title: 'Typical Workflow Summary',
    steps: [
      'SharpeMetrix → Publishes model weights & rebalances (via CSV export, with API/platform integration coming soon)',
      'RIA Advisor → Downloads models and pushes to Distribution Platform (Custodian, TAMP, or BD)',
      'Distribution Platform → Receives models and implements account-level trading',
      'RIA Advisor → Selects appropriate model for each client based on suitability',
      'Client Accounts → Trades executed automatically with ongoing drift management and billing'
    ]
  };

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Workflow Integration</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <UserGroupIcon className="h-8 w-8 text-primary-100" />
            Workflow Integration for Advisors
          </h1>
          <p className="text-lg text-text-200">
            Complete implementation guide for integrating SharpeMetrix models into your advisory practice.
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8 bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-4">Advisor Onboarding to SharpeMetrix Model Portfolios</h2>
          <p className="text-text-200 leading-relaxed">
            This comprehensive guide walks advisors through the complete process of implementing SharpeMetrix models, 
            from initial selection through ongoing maintenance and client service. Follow these steps to ensure successful 
            integration with your existing processes and technology stack.
          </p>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-4 mb-8">
          {workflowSteps.map((step) => (
            <div key={step.id} className="bg-bg-200 rounded-lg border border-bg-300">
              <button
                onClick={() => toggleSection(step.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-bg-300 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <step.icon className="h-5 w-5 text-primary-100" />
                  <div>
                    <h3 className="font-semibold text-text-100">{step.name}</h3>
                    <p className="text-sm text-text-200 mt-1">{step.description}</p>
                  </div>
                </div>
                {expandedSections[step.id] ? (
                  <ChevronDownIcon className="h-5 w-5 text-text-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-text-200" />
                )}
              </button>
              {expandedSections[step.id] && (
                <div className="px-6 pb-4">
                  <div className="text-text-200 leading-relaxed whitespace-pre-line">
                    {step.details}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Workflow Summary */}
        <div className="bg-accent-100/5 rounded-lg p-6 border border-accent-100/20">
          <h3 className="text-lg font-semibold text-text-100 mb-4 flex items-center gap-2">
            <ArrowPathIcon className="h-5 w-5 text-accent-100" />
            {workflowSummary.title}
          </h3>
          <div className="space-y-3">
            {workflowSummary.steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-accent-100/20 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-accent-100">{index + 1}</span>
                </div>
                <p className="text-sm text-text-200 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default WorkflowIntegrationPage; 